import { load } from 'cheerio';
import { CheerioAPI } from 'cheerio';
import type { Element } from 'domhandler';
import { useSettingsStore } from '@/store/settings/settingsStore';

/**
 * Enhanced ERS description extraction preserving DOM order and avoiding duplicates.
 */
export function extractERSPageDescription(htmlSource: string): string {
    const $ = load(htmlSource);
    const settingsStore = useSettingsStore();
    const isDebug = settingsStore.settings.useDebug;
    let descriptionHtml = '';

    function appendContent(content: string, source: string) {
        if (!content) {
            if (isDebug) console.log(`Content from ${source} is empty.`);
            return;
        }
        if (isDebug) console.log(`Appending content from: ${source}`, content);
        descriptionHtml += content;
    }

    const blockSpecs: Array<{
        selector: string;
        sourceLabel: string;
        process: ($: CheerioAPI, el: Element) => string;
    }> = [
            {
                selector: 'div.ers-full-width-image',
                sourceLabel: 'Block -3 Full-Width Image',
                process: ($, el) => $.html($(el)).trim(),
            },
            {
                selector: 'section#textimage2',
                sourceLabel: 'Block -2: textimage2',
                process: ($, el) => $.html($(el).clone()).trim(),
            },
            {
                selector: 'div.image-right-text-left-new',
                sourceLabel: 'Block -1: image-right-text-left-new',
                process: ($, el) => $.html($(el)).trim(),
            },
            {
                selector: 'section',
                sourceLabel: 'Block 0: generic-section individual section',
                process: ($, el) => {
                    const $el = $(el).clone();
                    if (
                        $el.hasClass('contact-form-recaptcha') ||
                        $el.find('.contact-form-recaptcha').length > 0 ||
                        $el.hasClass('store') ||
                        $el.find('.store').length > 0
                    ) {
                        return '';
                    }
                    return $el.find('.container').length > 0 ? $.html($el).trim() : '';
                },
            },
            {
                selector: 'section.video-feature',
                sourceLabel: 'Block 1: video-feature',
                process: ($, el) => {
                    const $el = $(el).clone();
                    $el.find(
                        '.ers_weather, .contact-form, .contact-form-recaptcha, .ers-header, .navbar-top, .master-nav, ' +
                        '.header-top, .mds-template-header, .elemtype-Footer, style, .button, script, .editor-only, ' +
                        '.service-col-right, .overlay-embed'
                    ).remove();
                    return $.html($el).trim();
                },
            },
            {
                selector: 'div.ers-container',
                sourceLabel: 'Block 2: ers-container',
                process: ($, el) => {
                    const $clone = $(el).clone();
                    $clone.find('.application-form').remove();
                    return $clone.html()?.trim() || '';
                },
            },
            {
                selector: 'section[class^="article-"]',
                sourceLabel: 'Block 3: article-section',
                process: ($, el) => $(el).clone().html()?.trim() || '',
            },
            {
                selector: 'div#elemtype-3ColTextBoxes',
                sourceLabel: 'Block 4: 3ColTextBoxes',
                process: ($, el) => {
                    const $el = $(el).clone();
                    const content = $el.find('.container');
                    return content.length
                        ? `<div class="three-col-text-boxes">${$.html(content)}</div>`
                        : '';
                },
            },
            {
                selector: 'section.3coltextboxes',
                sourceLabel: 'Block 5: 3coltextboxes',
                process: ($, el) => {
                    const $el = $(el).clone();
                    return $el.find('.container').length
                        ? `<section class="three-col-text-boxes">${$.html($el.find('.container'))}</section>`
                        : '';
                },
            },
            {
                selector: 'section.product-feature',
                sourceLabel: 'Block 6: product-feature',
                process: ($, el) => {
                    const $el = $(el).clone();
                    $el.find('a.btn, .row-spacing, script, style').remove();
                    return `<div class="product-feature-section">${$el.find('img.img-responsive').length
                        ? $.html($el.find('img.img-responsive'))
                        : ''
                        }${$el.find('.h4').length
                            ? $.html($el.find('.h4'))
                            : ''
                        }${$el.find('[id$="_textid-text1"]').length
                            ? $.html($el.find('[id$="_textid-text1"]'))
                            : ''
                        }</div>`;
                },
            },
            {
                selector: 'div[class^="ers-read-more-"]',
                sourceLabel: 'Block 7: read-more-section',
                process: ($, el) => {
                    const $el = $(el).clone();
                    $el.find('.show-more-button-container, .show-more-button, script, style').remove();
                    const header = $el.find('.read-more-header');
                    const content = $el.find('.read-more-text');
                    return (header.length || content.length)
                        ? `<div class="read-more-section">${$.html(header)}${$.html(content)}</div>`
                        : '';
                },
            },
            {
                selector: 'div.ers-service-area',
                sourceLabel: 'Block 8: ers-service-area',
                process: ($, el) => $.html($(el)).trim(),
            },
            {
                selector: 'section[class^="premium-video"]',
                sourceLabel: 'Block 9: premium-video',
                process: ($, el) => $.html($(el)).trim(),
            },
            {
                selector: 'div[id^="seoReadMore_id_"]',
                sourceLabel: 'Block 10: seo-read-more',
                process: ($, el) => {
                    const $el = $(el).clone();
                    const title = $el.find('[id$="_textid-seo-rm-title"]');
                    const desc = $el.find('[id$="_textid-seo-rm-description"]');
                    return (title.length || desc.length)
                        ? `<div class="seo-read-more-section">${$.html(title)}${$.html(desc)}</div>`
                        : '';
                },
            },
            {
                selector: 'div[id^="seoImageContent_id_"]',
                sourceLabel: 'Block 11: seo-image-content',
                process: ($, el) => {
                    const cards = $(el).clone().find('.seo-ic-image, .seo-ic-content');
                    return cards.length ? $.html(cards).trim() : '';
                },
            },
            {
                selector: 'div[id^="seoItemCards_id_"]',
                sourceLabel: 'Block 12: seo-item-cards',
                process: ($, el) => {
                    const cards = $(el).clone().find('.seo-card-item');
                    return cards.length ? $.html(cards).trim() : '';
                },
            },
            {
                selector: 'div[id^="seoContactDetails_id_"]',
                sourceLabel: 'Block 13: seo-contact-details',
                process: ($, el) => {
                    const $el = $(el).clone();
                    const content = $el.find('.seo-cd-title, .seo-cd-content, .seo-cd-list');
                    return content.length ? $.html(content).trim() : '';
                },
            },
            {
                selector: 'div[id^="elemtype-HeaderText"], div#elemtype-TwoLists',
                sourceLabel: 'Block 14: headerText/TwoLists',
                process: ($, el) => $(el).html()?.trim() || '',
            },
            {
                selector: 'section.headertext2',
                sourceLabel: 'Block 15: headertext2',
                process: ($, el) => $(el).html()?.trim() || '',
            },
            {
                selector: 'section.text-area',
                sourceLabel: 'Block 16: text-area',
                process: ($, el) => $(el).html()?.trim() || '',
            },
            {
                selector: 'section[class^="banner"]',
                sourceLabel: 'Block 17: banner',
                process: ($, el) => $(el).html()?.trim() || '',
            },
            {
                selector: 'section.accordion-section',
                sourceLabel: 'Block 18: accordion-section',
                process: ($, el) => {
                    const panels = $(el).clone().find('.panel.panel-default');
                    return panels.length ? $.html($(el)).trim() : '';
                },
            },
            {
                selector: 'div.container:has(div.column-list)',
                sourceLabel: 'Block 19: container-column-list',
                process: ($, el) => {
                    const $el = $(el).clone();
                    return $el.find('div.service-area-column').length
                        ? $.html($el).trim()
                        : '';
                },
            },
            {
                selector: 'section.feature-section',
                sourceLabel: 'Block 20: feature-section',
                process: ($, el) => $(el).clone().find('.container').length ? $.html($(el)).trim() : '',
            },
            {
                selector: 'section#textimage2',
                sourceLabel: 'Block 21: textimage2',
                process: ($, el) => $(el).clone().find('.container-fluid').length ? $.html($(el)).trim() : '',
            },
            {
                selector: 'div.imageBlockContainer',
                sourceLabel: 'Block 22: imageBlockContainer',
                process: ($, el) => $(el).clone().html()?.trim() || '',
            },
            {
                selector: 'section.headertext1',
                sourceLabel: 'Block 23: headertext1',
                process: ($, el) => $(el).clone().html()?.trim() || '',
            },
            {
                selector: 'div[id^="elemtype-HeaderText"]',
                sourceLabel: 'Block 24: additional headerText',
                process: ($, el) => $(el).clone().html()?.trim() || '',
            },
            {
                selector: 'div[class*="image-wrapper"]',
                sourceLabel: 'Block 25: image-wrapper',
                process: ($, el) => $(el).clone().html()?.trim() || '',
            },
            {
                selector: 'div.ers-text-left-image-right',
                sourceLabel: 'Block 26: text-left-image-right',
                process: ($, el) => $.html($(el)).trim(),
            },
            {
                selector: '.faq',
                sourceLabel: 'Block 27: faq',
                process: ($, el) => $.html($(el)).trim(),
            },
            {
                selector: '.ers-text-heading-span',
                sourceLabel: 'Block 28: ers-text-heading-span',
                process: ($, el) => $.html($(el)).trim(),
            },
            {
                selector: 'div.content_holder',
                sourceLabel: 'Block: content_holder',
                process: ($, el) => $.html($(el)).trim(),
            },
        ];

    const combinedSelector = blockSpecs.map(s => s.selector).join(', ');
    const elements = $(combinedSelector).toArray();
    const processedNodes = new Set<Element>();

    elements.forEach(el => {
        if (el.type !== 'tag') return;
        const ancestors = $(el).parents().toArray();
        if (ancestors.some(a => processedNodes.has(a))) return;
        if (
            $(el).is('section.top-section-container') ||
            $(el).is('footer') ||
            $(el).is('.copyright-container') ||
            $(el).is('section.footer')
        ) return;

        const spec = blockSpecs.find(s => $(el).is(s.selector));
        if (spec) {
            const processed = spec.process($, el);
            if (processed) {
                appendContent(processed, spec.sourceLabel);
                processedNodes.add(el);
            }
        }
    });

    return descriptionHtml;
}
